import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/ui-kit";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtTime } from "@/components/Pager";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, ClipboardList, Wallet, CalendarCheck, GraduationCap, Download, Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, History, Undo2, Trash2 } from "lucide-react";

const KINDS = {
  students: { label: "Students", icon: Users,
    cols: ["sr_no", "student_id", "indent_id", "student_name", "parent_name", "mother_name", "mobile_number", "class_name", "section", "admission_date", "uniform_size", "kit_taken", "date_of_birth", "branch", "school_type", "parent_email", "address", "emergency_contact", "medical_info", "student_status", "academic_year"],
    head: ["Sr No", "Student ID", "Indent Id", "Student Name", "Parent Name", "Mother Name", "Mobile Number", "Class Name", "Section", "Admission Date", "Uniform Size", "Kit Taken", "Date of Birth", "Branch", "School Type", "Parent Email", "Address", "Emergency Contact", "Medical Info", "Student Status", "Academic Year"],
    sample: ["1", "ADM-2026-101", "IND-0044", "Aditi Sharma", "Rakesh Sharma", "", "9876543210", "LKG", "A", "2026-06-01", "Girls 24", "Yes", "", "SPK-001", "", "parent@example.com", "", "", "", "active", "2026-27"],
    note: "Gender is read from Uniform Size (Boys 24 / Girls 24, B-26 / G-26). Mother Name, Date of Birth and Address are optional. Section blank = A. A class not yet in the app is added automatically. Kit Taken: Yes or No. Status: active, inactive, alumni, transferred." },
  admissions: { label: "Admission details", icon: ClipboardList, needsBranch: true,
    cols: ["sr_no", "student_id", "student_name", "parent_name", "class_name", "indent_id", "mobile_number", "admission_date", "uniform_size", "kit_taken", "app_status"],
    head: ["Sr No", "Student ID", "Student Name", "Parent Name", "Class Name", "Indent Id", "Mobile Number", "Admission Date", "Uniform Size", "Kit Taken", "App Status"],
    sample: ["1", "ADM-2026-102", "Kabir Rao", "Suresh Rao", "LKG-B", "IND-0045", "9876543210", "2026-06-01", "M", "No", "Confirmed"],
    note: "Choose the branch first. A new Student ID adds a student; an existing Student ID updates that student. Section can be written with the class (LKG-B); otherwise it is A. App Status: Enquiry, Applied, Under review, Waitlisted, Confirmed, Admitted, Cancelled, Rejected." },
  fees: { label: "Fee history", icon: Wallet, cols: ["admission_number", "branch", "academic_year", "fee_structure", "total_fee", "tuition_fee", "annual_fee", "admission_fee", "activity_fee", "transport_fee", "other_fee", "discount", "frequency", "first_due_date", "amount_paid", "payment_date", "payment_method", "receipt_number", "outstanding_due"],
    sample: ["ADM-2026-101", "SPK-001", "2025-26", "Playschool standard", "50000", "36000", "8000", "0", "3000", "3000", "0", "0", "quarterly", "2025-06-10", "20000", "2025-06-08", "upi", "RCPT-0001", "30000"],
    note: "One row per payment. Repeat the student + academic year on each payment row; the fee structure is taken from the first row. Leave the fee parts blank to treat the total fee as tuition. Methods: cash, UPI, card, bank transfer, cheque." },
  attendance: { label: "Attendance history", icon: CalendarCheck, cols: ["admission_number", "branch", "date", "status"],
    sample: ["ADM-2026-101", "SPK-001", "2026-07-01", "Present"], note: "One row per student per day. Status: Present, Absent, Late or Leave (P / A / L / LV also work)." },
  marks: { label: "Marks history", icon: GraduationCap, cols: ["admission_number", "branch", "term", "subject", "marks", "max_marks", "grade", "remarks"],
    sample: ["ADM-2026-101", "SPK-001", "Term 1", "Mathematics", "86", "100", "", "Good progress"], note: "One row per subject per term. Leave grade blank to calculate it from the marks." },
};

function downloadTemplate(kind) {
  const k = KINDS[kind];
  const ws = XLSX.utils.aoa_to_sheet([k.head || k.cols, k.sample]);
  const notes = XLSX.utils.aoa_to_sheet([["How to fill this sheet"], [k.note], ["Delete the sample row before importing."]]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.utils.book_append_sheet(wb, notes, "Instructions");
  XLSX.writeFile(wb, `sparkora-${kind}-import-template.xlsx`);
}

async function readFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true, raw: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: "", raw: false, dateNF: "yyyy-mm-dd" });
}

const norm = (k) => String(k).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const ALT = { student_name: "name", student_id: "admission_number", parent_name: "father_name", mobile_number: "parent_phone", class_name: "class", kit_taken: "kit" };

function Cell({ r, c, onEdit }) {
  const v = r.data[c] || r.data[ALT[c]] || "";
  const bad = !v && r.errors.some((e) => e.toLowerCase().includes(c.split("_")[0]));
  return (
    <td className={`p-1 ${bad ? "bg-rose-100" : ""}`}>
      <input key={v} data-testid={`cell-${r.row}-${c}`} defaultValue={v} onBlur={(e) => e.target.value !== v && onEdit(r.row, c, e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-full min-w-[110px] bg-transparent px-1.5 py-1 rounded border border-transparent hover:border-slate-200 focus:border-violet-400 focus:bg-white outline-none" />
    </td>
  );
}

function Preview({ kind, res, onlyIssues, onEdit, onRemove }) {
  const { cols, head } = KINDS[kind];
  const rows = onlyIssues ? res.rows.filter((r) => r.errors.length || r.warnings.length) : res.rows;
  return (
    <div className="overflow-auto max-h-[480px] rounded-xl border border-slate-100" data-testid="import-preview">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500"><th className="p-2" /><th className="p-2">Row</th><th className="p-2">Check</th>{cols.map((c, i) => <th key={c} className="p-2 whitespace-nowrap">{head ? head[i] : c}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.row} data-testid={`preview-row-${r.row}`} className={`border-t border-slate-100 align-top ${r.errors.length ? "bg-rose-50" : r.warnings.length ? "bg-amber-50" : ""}`}>
            <td className="p-2"><button data-testid={`remove-row-${r.row}`} title="Remove this row" onClick={() => onRemove(r.row)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button></td>
            <td className="p-2 font-mono">{r.row}</td>
            <td className="p-2 min-w-[220px]">
              {r.errors.length === 0 && r.warnings.length === 0 && <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Ready</span>}
              {r.errors.map((e, i) => <div key={i} className="text-rose-700 flex gap-1"><XCircle size={13} className="shrink-0 mt-0.5" /> {e}</div>)}
              {r.warnings.map((e, i) => <div key={i} className="text-amber-700 flex gap-1"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {e}</div>)}
            </td>
            {cols.map((c) => <Cell key={c} r={r} c={c} onEdit={onEdit} />)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function HistoryLog({ refresh }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/imports/history").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refresh]);
  async function undo(x) {
    if (!window.confirm(`Undo the ${KINDS[x.kind]?.label} import "${x.file_name}"? This removes the ${x.imported} records it added.`)) return;
    setBusy(x.id);
    try { const r = await api.post(`/imports/${x.id}/undo`); toast.success(`Undone: ${r.data.removed} records removed`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="import-history">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3"><History size={17} className="text-violet-700" /> Import history</h3>
      {items.length === 0 ? <div className="text-sm text-slate-500">No imports yet.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Date</th><th>Type</th><th>File</th><th>Imported</th><th>Skipped</th><th>By</th><th /></tr></thead>
          <tbody>{items.map((x) => <tr key={x.id} data-testid={`import-log-${x.id}`} className={`border-t border-slate-100 ${x.undone_at ? "opacity-60" : ""}`}><td className="py-2">{fmtTime(x.created_at)}</td><td>{KINDS[x.kind]?.label}</td>
            <td className="max-w-[220px] truncate">{x.file_name}</td><td className="font-semibold text-emerald-700">{x.imported}</td><td className="text-slate-500">{x.skipped}</td><td>{x.imported_by_name}</td>
            <td className="text-right">{x.undone_at ? <span data-testid={`import-undone-${x.id}`} className="text-xs text-slate-400">Undone {fmtTime(x.undone_at)} by {x.undone_by_name}</span>
              : <Button data-testid={`import-undo-${x.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={busy === x.id} onClick={() => undo(x)}>
                {busy === x.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Undo2 size={14} className="mr-1" />} Undo</Button>}</td></tr>)}</tbody></table></div>
      )}
    </div>
  );
}

export default function ImportPage() {
  const [kind, setKind] = useState("students");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const input = useRef(null);
  useEffect(() => { api.get("/branches").then((r) => { setBranches(r.data); if (r.data.length === 1) setBranchId(r.data[0].id); }).catch(() => {}); }, []);
  const reset = () => { setFile(null); setRows(null); setRes(null); if (input.current) input.current.value = ""; };

  async function pick(f) {
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) return toast.error("Choose a CSV or Excel file");
    if (!branchId) { if (input.current) input.current.value = ""; return toast.error("Choose a branch first"); }
    setBusy(true); setFile(f); setRes(null);
    try {
      const data = await readFile(f);
      if (!data.length) throw new Error("The file has no rows");
      if (data.length > 5000) throw new Error("Import up to 5000 rows at a time");
      setRows(data);
      setRes((await api.post(`/imports/${kind}/validate`, { file_name: f.name, rows: data, branch_id: branchId || undefined })).data);
    } catch (e) { toast.error(e.response ? formatApiError(e) : e.message); reset(); } finally { setBusy(false); }
  }

  async function revalidate(next) {
    setRows(next); setBusy(true);
    try { setRes((await api.post(`/imports/${kind}/validate`, { file_name: file.name, rows: next, branch_id: branchId || undefined })).data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  function editCell(rowNum, col, val) {
    const next = [...rows];
    const raw = { ...next[rowNum - 2] };
    const keys = Object.keys(raw);
    const key = keys.find((k) => norm(k) === col) || keys.find((k) => norm(k) === ALT[col]) || col;
    raw[key] = val;
    next[rowNum - 2] = raw;
    revalidate(next);
  }
  function removeRow(rowNum) {
    const next = [...rows];
    next[rowNum - 2] = {};
    revalidate(next);
  }

  async function confirm() {
    if (!window.confirm(`Import ${res.summary.valid} valid rows? ${res.summary.invalid ? `${res.summary.invalid} rows with errors will be skipped.` : ""}`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/imports/${kind}/commit`, { file_name: file.name, rows, branch_id: branchId || undefined });
      toast.success(`Imported ${r.data.imported} records`); reset(); setRefresh((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  const K = KINDS[kind];
  return (
    <AppShell>
      <div data-testid="import-page" className="space-y-5">
        <PageHeader eyebrow="Data" title="Import Students & History" subtitle="Upload CSV or Excel files, check every row, then confirm to save." />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {Object.entries(KINDS).map(([k, v]) => (
            <button key={k} data-testid={`import-kind-${k}`} onClick={() => { setKind(k); reset(); }}
              className={`flex items-center gap-3 p-4 rounded-2xl border transition-colors text-left ${kind === k ? "bg-violet-600 text-white border-violet-600 shadow-md" : "bg-white border-violet-100 hover:border-violet-300"}`}>
              <v.icon size={20} /><span className="font-semibold text-sm">{v.label}</span>
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-[240px]"><div className="font-display font-bold text-slate-900">{K.label} import</div><div className="text-sm text-slate-500 mt-1">{K.note}</div>
              <div className="text-xs text-slate-400 mt-2">Columns: {(K.head || K.cols).join(", ")}</div></div>
            <Button data-testid="download-template" variant="outline" onClick={() => downloadTemplate(kind)}><Download size={15} className="mr-1" /> Download template</Button>
          </div>
          <div data-testid="import-branch-step" className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
            <span className="text-sm font-semibold text-violet-900">Step 1 · Choose branch</span>
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); reset(); }}>
              <SelectTrigger data-testid="import-branch" className="w-60 bg-white"><SelectValue placeholder="Choose branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-xs text-slate-500">Rows with a blank branch column use this branch.</span>
          </div>
          <label data-testid="import-drop" className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-2xl py-8 transition-colors ${branchId ? "border-violet-200 cursor-pointer hover:bg-violet-50/50" : "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"}`}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
            {busy ? <Loader2 className="animate-spin text-violet-600" /> : <Upload className="text-violet-600" />}
            <span className="text-sm font-semibold text-slate-700">{file ? file.name : branchId ? "Step 2 · Choose or drop a CSV / Excel file" : "Choose a branch first to upload"}</span>
            <input ref={input} data-testid="import-file" type="file" accept=".csv,.xlsx,.xls" disabled={!branchId} className="hidden" onChange={(e) => pick(e.target.files[0])} />
          </label>
          {res && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2" data-testid="import-summary">
                <Badge className="bg-slate-100 text-slate-700">{res.summary.total} rows</Badge>
                <Badge className="bg-emerald-100 text-emerald-800">{res.summary.valid} ready</Badge>
                <Badge className="bg-rose-100 text-rose-700">{res.summary.invalid} with errors</Badge>
                <Badge className="bg-amber-100 text-amber-800">{res.summary.warnings} with warnings</Badge>
                <label className="text-xs text-slate-600 flex items-center gap-1 ml-2"><input data-testid="only-issues" type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> Show only rows with issues</label>
                <span className="flex-1" />
                <Button data-testid="import-cancel" variant="outline" onClick={reset} disabled={busy}>Cancel</Button>
                <Button data-testid="import-confirm" className="btn-brand" onClick={confirm} disabled={busy || !res.summary.valid}>Confirm & import {res.summary.valid} rows</Button>
              </div>
              <p className="text-xs text-slate-500">Click any cell to fix it before importing; the row is re-checked when you leave the cell. Use the bin to drop a row.</p>
              <Preview kind={kind} res={res} onlyIssues={onlyIssues} onEdit={editCell} onRemove={removeRow} />
            </div>
          )}
        </div>
        <HistoryLog refresh={refresh} />
      </div>
    </AppShell>
  );
}
